import { useState } from "react"
import PropTypes from "prop-types"
import { get, includes, without, isEmpty, size } from "lodash"
import { Button, Card, CardContent, CardActions, Typography } from "@mui/material"
import { RESOURCE_LIMITS } from "helpers/shapes"
import { PortfolioList } from "./components"
import LoadingCard from "components/loading-card"
import ResourcesChip from "components/resources-chip"

import "./style.scss"

export default function EzeImport({
  account,
  isOpPro,
  portfolios,
  resourceLimits,
  connected,
  importPortfolios,
  importing,
}) {
  const [portfoliosToImport, setPortfoliosToImport] = useState([])

  function getAuthenticatedStatus() {
    if (connected) {
      return {
        color: "success",
        text: "Connected",
      }
    }

    return {
      color: "warning",
      text: "Not connected",
    }
  }

  function contactUsAuth() {
    window.Intercom("showNewMessage", `Hello Omega Point, I am interested in authenticating the Eze integration for the account "${account}". Please reply with next steps, including connecting with Eze to obtain login credentials for this integration.`)
  }

  function contactUsPortfolioImport() {
    window.Intercom("showNewMessage", `Hello Omega Point, I am currently experiencing issues with the Eze import for the account "${account}".`)
  }

  function togglePortfolio(portfolio) {
    const ezePortfolioId = get(portfolio, "ezePortfolioId")
    const newPortfolioList = includes(portfoliosToImport, ezePortfolioId) ? without(portfoliosToImport, ezePortfolioId) : [...portfoliosToImport, ezePortfolioId]
    setPortfoliosToImport(newPortfolioList)
  }

  function getPortfolioListStatus() {
    const { loading, error } = portfolios

    if (loading) {
      return { text: "Requesting list...", color: "primary" }
    }

    if (error) {
      return { text: "Error", color: "error" }
    }

    return { text: "List received", color: "success" }
  }

  function importCTA() {
    if (!connected || portfolios.loading || portfolios.error) {
      return null
    }

    return <CardActions>
      <Button disabled={isEmpty(portfoliosToImport) || importing} color="primary" onClick={() => importPortfolios(portfoliosToImport)}>Start import</Button>
    </CardActions>
  }

  return <div className="modules_eze-import">
    <div className="content">
      <Card square>
        <div className="header no-loader">
          <Typography variant="h2" color="textPrimary" gutterBottom>
            About this app
          </Typography>
        </div>
        <CardContent>
          <Typography>
            This integration allows mutual customers to import their portfolios from Eze into Omega Point&apos;s ecosystem on a daily basis.
          </Typography>
        </CardContent>
      </Card>

      <LoadingCard title="Authentication" status={getAuthenticatedStatus()} visible={!connected}>
        <Typography>
          Omega Point has been notified that this application has been activated and will configure credentials to authenticate the Eze integration on this account. Reach out to <a onClick={contactUsAuth}>Customer Success</a> directly to follow-up.
        </Typography>
        <Typography variant="subtitle2" color="textSecondary">Note: once activated, this integration is enabled across the entire account. As resources are created, account administrators can restrict access through permissions.</Typography>
      </LoadingCard>

      <LoadingCard title="Select portfolios for import" status={getPortfolioListStatus()} visible={connected && !portfolios.loading} loading={portfolios.loading} disabled={!connected}>
        {portfolios.error ? <Typography>An error occured while fetching data from Eze. <br />Please contact <a onClick={contactUsPortfolioImport}>Customer Success</a> if the error persists.</Typography>
          : <>
            <ResourcesChip resourceLimits={resourceLimits} newResourceCount={size(portfoliosToImport)} />
            <PortfolioList portfolios={portfolios.items} togglePortfolio={togglePortfolio} toImport={portfoliosToImport} availableResourceSlots={get(resourceLimits, "available")} isOpPro={isOpPro} />
          </>}
        {importCTA()}
      </LoadingCard>
    </div>
  </div>
}

EzeImport.propTypes = {
  account: PropTypes.string.isRequired,
  portfolios: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    error: PropTypes.bool,
    items: PropTypes.arrayOf(PropTypes.shape({
      ezePortfolioId: PropTypes.string.isRequired,
    }).isRequired).isRequired,
  }),
  resourceLimits: RESOURCE_LIMITS,
  connected: PropTypes.bool,
  importPortfolios: PropTypes.func.isRequired,
  importPortfoliosSuccess: PropTypes.func.isRequired,
  importing: PropTypes.bool.isRequired,
  isOpPro: PropTypes.bool.isRequired,
}
